import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Target, Eye, Heart, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function AboutPage() {
  const [ scrollY, setScrollY ] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: `To formalize and secure digital campus communication through a unified platform built exclusively for educational institutions.
      We aim to eliminate fragmentation, protect institutional integrity, and create seamless collaboration between students, faculty, administration, and alumni.`
    },
    {
      icon: Eye,
      title: 'Our Vision',
      description: [
        "To become the digital backbone of modern campuses worldwide — where education evolves without compromising structure, professionalism, or security.",
        "We envision institutions operating on intelligent, secure networks designed specifically for academic ecosystems."
      ]
    },
    {
      icon: Heart,
      title: 'Our Values',
      description: `Institutional Integrity
Education deserves platforms that respect its hierarchy, structure, and decorum.

Security by Design
Identity protection, controlled access, and secure communication are foundational — not optional.

Clarity Over Noise
Structured communication ensures no message is lost and no update goes unseen.

Balanced Innovation
Technology should enhance campuses — not replace their human foundation.`,
    },
    {
      icon: Zap,
      title: 'Our Approach',
      description: `We do not adapt corporate tools for classrooms.
We build infrastructure specifically for academic environments.

Our approach is simple:

Formalize digital communication

Implement layered and controlled access

Centralize resources securely

Preserve professionalism in every interaction

Integrate technology without compromising institutional standards

UNILinkd is not just another app.
It is the balanced digital framework education has been missing.`,
    },
  ];

  return (
    <div className="flex flex-col  bg-brand-yellow">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-12">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            backgroundImage: 'radial-gradient(circle, #004CFF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="container relative z-10">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-5xl font-extrabold tracking-tight text-brand-black md:text-6xl text-brand-black">
              About UNILinkd
            </h1>
            <p className="text-xl font-medium text-brand-black md:text-2xl">
              Transforming education through innovative technology and meaningful connections
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section
        className="relative bg-brand-white py-12"
        style={{
          transform: `translateY(${scrollY * 0.1}px)`,
        }}
      >
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-6 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black text-brand-black">Our Story</h2>
            <div className="space-y-4 text-lg font-medium text-brand-black">
              <p>
                UNILinkd was created from a simple but urgent realization: as education expanded into digital spaces, the structure, decorum, and security of academic institutions did not always follow.
              </p>
              <p>
                Campuses began relying on informal messaging platforms and scattered tools never designed to uphold institutional standards — leading to fragmented communication, missed updates, unprofessional exchanges, and growing concerns around data privacy, impersonation, harassment, and security breaches.
              </p>
              <p>
                We believe digital transformation should not dilute academic integrity.
                It should strengthen it.
              </p>
              <p>
                UNILinkd is a structured, secure, and purpose-built digital extension of your campus — designed to integrate modern digital behaviors with the discipline and clarity of traditional education.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow ">
        <div
          className="absolute inset-0 opacity-5 "
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
            backgroundImage: 'linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF), linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF)',
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px',
          }}
        />
        <div className="container relative z-10 ">
          <div className="mb-8 text-center">
            <h2 className="mb-3 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black text-brand-black">What Drives Us</h2>
            <p className="text-xl font-medium text-brand-black">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {values.map((value, index) => (
              <Card key={index} className="rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl">
                <CardHeader>
                  <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-lg">
                    <value.icon className="h-8 w-8 text-brand-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-brand-black">{value.title}</CardTitle>
                  <CardDescription className="text-base font-medium text-brand-black">{value.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section
        className="relative bg-brand-white py-12"
        style={{
          //transform: `translateY(${scrollY * 0.15}px)`,
        }}
      >
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black text-brand-black">Join Our Team</h2>
            <p className="mb-6 text-xl font-medium text-brand-black">
              We're always looking for passionate individuals who share our vision of transforming
              education. If you're excited about making a difference in how students learn and connect,
              we'd love to hear from you.
            </p>
            <a
              href="/join"
              className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] px-8 py-4 text-lg font-bold text-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
            >
              View Open Positions
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
